export interface BirthData {
  birthdate: string;
  birth_time: string;
  birth_place: string;
}

export interface UserProfile {
  user_goal?: string;
  tarot_experience?: string;
  deck_preference?: string;
  birth_data?: BirthData;
  reading_frequency?: string;
  account?: string;
  ai_tone_preference?: string;
  credit_intro_ack?: boolean;
  privacy_consent?: boolean;
  language?: string; // Keep for backward compatibility
}

export interface CreditHistory {
  action: string;
  type: 'credit' | 'debit';
  amount: number;
}

export interface UserEntityData {
  _id: string;
  email?: string;
  username?: string;
  password: string;
  firstname: string;
  lastname: string;
  birthdate: Date | null;
  credits: number;
  lastUpdated: Date | null;
  picture: string;
  slug?: string;
  __v?: number;
  street?: string;
  number?: string;
  complement?: string;
  zipCode?: string;
  city?: string;
  state?: string;
  country?: string;
  gender?: string;
  language?: string; // Add language field at top level (format: en-US, pt-BR, etc.)
  creditHistory?: CreditHistory[];
  personalizedQuote?: string;
  onboardCompleted?: boolean;
  onboarding: boolean;
  favoriteReadings?: Array<{ type: string, count: number }>;
  profile?: UserProfile;
  dailyReading?: { _id: string, cards: Array<{ id: number, reversed: boolean }>, quote?: string, interpretation?: { detailed_reading: { final_conclusion: string } } };
}

export class UserEntity implements UserEntityData {
  private __id: string;
  private _email: string;
  private _username: string;
  private _password: string;
  private _firstname: string;
  private _lastname: string;
  private _birthdate: Date | null;
  private _credits: number;
  private _lastUpdated: Date | null;
  private _picture: string;
  private _slug: string;
  private _version: number;
  private _street: string;
  private _number: string;
  private _complement: string;
  private _zipCode: string;
  private _city: string;
  private _state: string;
  private _country: string;
  private _gender: string;
  private _language: string; // Add language field
  private _creditHistory: CreditHistory[];
  private _personalizedQuote?: string;
  private _onboardCompleted?: boolean;
  private _onboarding: boolean;
  private _favoriteReadings?: Array<{ type: string, count: number }>;
  private _profile?: UserProfile;
  private _dailyReading?: { _id: string, cards: Array<{ id: number, reversed: boolean }>, quote?: string, interpretation?: { detailed_reading: { final_conclusion: string } } };
  constructor(data: UserEntityData) {
    this.__id = data._id;
    this._email = data.email || '';
    this._username = data.username || data.email || '';
    this._password = data.password || '';
    this._firstname = data.firstname || '';
    this._lastname = data.lastname || '';
    this._birthdate = data.birthdate;
    this._credits = data.credits || 0;
    this._lastUpdated = data.lastUpdated;
    this._picture = data.picture || '';
    this._slug = data.slug || '';
    this._version = data.__v || 0;
    this._street = data.street || '';
    this._number = data.number || '';
    this._complement = data.complement || '';
    this._zipCode = data.zipCode || '';
    this._city = data.city || '';
    this._state = data.state || '';
    this._country = data.country || '';
    this._gender = data.gender || '';
    this._language = data.language || ''; // Initialize language field
    this._creditHistory = data.creditHistory || [];
    this._personalizedQuote = data.personalizedQuote;
    this._onboardCompleted = data.onboardCompleted;
    this._onboarding = data.onboarding || false;
    this._favoriteReadings = data.favoriteReadings;
    this._profile = data.profile;
    this._dailyReading = data.dailyReading;
  }

  // Getters
  get _id(): string {
    return this.__id;
  }

  get slug(): string {
    return this._slug;
  }

  get version(): number {
    return this._version;
  }

  get profile(): UserProfile | undefined {
    return this._profile;
  }

  get username(): string {
    return this._username;
  }

  get email(): string {
    return this._email;
  }

  get password(): string {
    return this._password;
  }

  get firstname(): string {
    return this._firstname;
  }

  get lastname(): string {
    return this._lastname;
  }

  get birthdate(): Date | null {
    return this._birthdate;
  }

  get credits(): number {
    return this._credits;
  }

  get lastUpdated(): Date | null {
    return this._lastUpdated;
  }

  get picture(): string {
    return this._picture;
  }

  get street(): string {
    return this._street;
  }

  get number(): string {
    return this._number;
  }

  get complement(): string {
    return this._complement;
  }

  get zipCode(): string {
    return this._zipCode;
  }

  get city(): string {
    return this._city;
  }

  get state(): string {
    return this._state;
  }

  get country(): string {
    return this._country;
  }

  get gender(): string {
    return this._gender;
  }

  get language(): string {
    return this._language;
  }

  get creditHistory(): CreditHistory[] {
    return this._creditHistory;
  }

  get personalizedQuote(): string | undefined {
    return this._personalizedQuote;
  }

  get onboardCompleted(): boolean {
    return this._onboardCompleted || false;
  }

  get onboarding(): boolean {
    return this._onboarding;
  }

  get dailyReading(): { _id: string, cards: Array<{ id: number, reversed: boolean }>, quote?: string, interpretation?: { detailed_reading: { final_conclusion: string } } } | undefined {
    return this._dailyReading;
  }

  get favoriteReadings(): Array<{ type: string, count: number }> | undefined {
    return this._favoriteReadings;
  }

  // Setters
  set slug(value: string) {
    this._slug = value;
  }

  set profile(value: UserProfile | undefined) {
    this._profile = value;
  }

  set password(value: string) {
    if (!value || value.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
    this._password = value;
  }

  set email(value: string) {
    if (!value || value.trim().length === 0) {
      throw new Error('Email cannot be empty');
    }
    this._email = value.trim();
  }

  set firstname(value: string) {
    if (!value || value.trim().length === 0) {
      throw new Error('Firstname cannot be empty');
    }
    this._firstname = value.trim();
  }

  set lastname(value: string) {
    if (!value || value.trim().length === 0) {
      throw new Error('Lastname cannot be empty');
    }
    this._lastname = value.trim();
  }

  set birthdate(value: Date | null) {
    if (value && value > new Date()) {
      throw new Error('Birthdate cannot be in the future');
    }
    this._birthdate = value;
  }

  set credits(value: number) {
    if (value < 0) {
      throw new Error('Credits cannot be negative');
    }
    this._credits = value;
  }

  set lastUpdated(value: Date | null) {
    this._lastUpdated = value;
  }

  set picture(value: string) {
    this._picture = value;
  }

  set street(value: string) {
    this._street = value;
  }

  set dailyReading(value: { _id: string, cards: Array<{ id: number, reversed: boolean }>, quote?: string, interpretation?: { detailed_reading: { final_conclusion: string } } }) {
    this._dailyReading = value;
  }

  set number(value: string) {
    this._number = value;
  }

  set complement(value: string) {
    this._complement = value;
  }

  set zipCode(value: string) {
    this._zipCode = value;
  }

  set city(value: string) {
    this._city = value;
  }

  set state(value: string) {
    this._state = value;
  }

  set country(value: string) {
    this._country = value;
  }

  set gender(value: string) {
    this._gender = value;
  }

  set language(value: string) {
    this._language = value;
  }

  set creditHistory(value: CreditHistory[]) {
    this._creditHistory = value;
  }

  set personalizedQuote(value: string | undefined) {
    this._personalizedQuote = value;
  }

  set onboardCompleted(value: boolean) {
    this._onboardCompleted = value;
  }

  set onboarding(value: boolean) {
    this._onboarding = value;
  }

  // Utility methods
  public isValid(): boolean {
    return (
      this._email?.trim().length >= 3 &&
      this._password?.length >= 8 &&
      this._firstname?.trim().length > 0 &&
      this._lastname?.trim().length > 0 &&
      this._credits >= 0 &&
      (!this._birthdate || this._birthdate <= new Date())
    );
  }

  public static getFromLocalStorage(): UserEntity {
    const userStr = localStorage.getItem('user');
    if (!userStr || userStr === '{}') {
      // Return a minimal UserEntity with default values instead of null
      return new UserEntity({
        _id: '',
        firstname: '',
        lastname: '',
        username: '',
        email: '',
        password: '',
        birthdate: null,
        credits: 0,
        lastUpdated: null,
        picture: '',
        onboarding: false
      });
    }
    try {
      const user = JSON.parse(userStr);

      // Merge daily reading data from separate localStorage key if it exists
      const dailyReadingStr = localStorage.getItem('dailyReading');
      if (dailyReadingStr) {
        try {
          user.dailyReading = JSON.parse(dailyReadingStr);
        } catch (dailyReadingError) {
          console.warn('Error parsing daily reading from localStorage:', dailyReadingError);
        }
      }

      return new UserEntity(user);
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      // Return default UserEntity on error
      return new UserEntity({
        _id: '',
        firstname: '',
        lastname: '',
        username: '',
        email: '',
        password: '',
        birthdate: null,
        credits: 0,
        lastUpdated: null,
        picture: '',
        onboarding: false
      });
    }
  }

  public static setToLocalStorage(user: UserEntity): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  public toString(): string {
    return JSON.stringify(
      {
        _id: this.__id,
        email: this._email,
        username: this._username,
        firstname: this._firstname,
        lastname: this._lastname,
        birthdate: this._birthdate,
        credits: this._credits,
        lastUpdated: this._lastUpdated,
        picture: this._picture,
        street: this._street,
        number: this._number,
        complement: this._complement,
        zipCode: this._zipCode,
        city: this._city,
        language: this._language,
        state: this._state,
        country: this._country,
        gender: this._gender,
        creditHistory: this._creditHistory,
        personalizedQuote: this._personalizedQuote,
        onboardCompleted: this._onboardCompleted,
        onboarding: this._onboarding,
        profile: this._profile,
        favoriteReadings: this._favoriteReadings
      },
      null,
      2
    );
  }

  public toJSON(): UserEntityData {
    return {
      _id: this.__id,
      username: this._username,
      email: this._email,
      password: this._password,
      firstname: this._firstname,
      lastname: this._lastname,
      birthdate: this._birthdate,
      credits: this._credits,
      lastUpdated: this._lastUpdated,
      picture: this._picture,
      slug: this._slug,
      __v: this._version,
      street: this._street,
      number: this._number,
      complement: this._complement,
      zipCode: this._zipCode,
      city: this._city,
      state: this._state,
      country: this._country,
      gender: this._gender,
      language: this._language,
      profile: this._profile,
      personalizedQuote: this._personalizedQuote,
      onboarding: this._onboarding,
      favoriteReadings: this._favoriteReadings
    };
  }
}
